<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="50%" :retain-focus="false">
      <v-skeleton-loader v-if="loading" type="article, actions" />
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.release.edit-release.edit_title_new")
                  : $t("wkt.admin.release.edit-release.edit_title_edit")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    :value="formatLongDateTime(data.creationDate)"
                    :label="$t('wkt.admin.release.edit-release.creation_date')"
                    autocomplete="none"
                    disabled
                    :rules="[]"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="3" style="margin-left: 120px;">
                  <span>{{ $t("wkt.admin.release.edit-release.enabled") }}</span>
                  <v-radio-group
                    v-model="data.enabled"
                    row
                    style="position: relative; top: -15px; max-height: 94px;"
                    :rules="[validationRules.requiredBoolean]"
                  >
                    <v-radio
                      :label="$t('wkt.admin.common.yes')"
                      :value="true"
                    />
                    <v-radio
                      :label="$t('wkt.admin.common.no')"
                      :value="false"
                    />
                  </v-radio-group>
                </v-col>
                
                <v-col cols="6" class="d-flex align-center">
                  <v-menu
                    ref="releaseDateMenu"
                    v-model="releaseDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="releaseDateFormatted"
                        :label="$t('wkt.admin.release.edit-release.release_date')"
                        v-on="on"
                        v-bind="attrs"
                        @blur="releaseDate = formatLocalDateToISOStringDate(releaseDateFormatted)"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="releaseDate"
                      :first-day-of-week="1" 
                      :locale="$i18n.locale"
                      no-title
                      @input="updateFormattedDate()"
                      @change="releaseDateMenu = false"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="data.title"
                    :label="$t('wkt.admin.release.edit-release.title')"
                    autocomplete="none"
                    auto-grow counter
                    :rules="[validationRules.required]"
                    maxLength="250"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="data.description"
                    :label="$t('wkt.admin.release.edit-release.description')"
                    autocomplete="none"
                    auto-grow counter
                    rows="2"
                    :rules="[validationRules.required]"
                    max="5000"
                    maxLength="5000"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            >{{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}</v-btn
          >
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
            >{{
              $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase()
            }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.release.edit-release.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.release.edit-release.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import api from "../../../api/worktime";

export default {
  name: "ReleaseEditorDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      loading: false,
      showDialog: false,
      companies: [],
      workplaces: [],
      data: {},
      msgCb: null,
      creationDate: null,
      releaseDate: null,
      releaseDateFormatted: null,
      releaseDateMenu: false
    };
  },
  methods: {
    open(action, payload) {
      this.showDialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      // Estamos creando un elemento nuevo
      if (!payload) {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayIso = `${year}-${month}-${day}`;

        this.releaseDate = todayIso;
        this.releaseDateFormatted = this.formatISOStringDateToLocalDate(todayIso);

        this.data = {
          creationDate: today,
          releaseDate: this.releaseDate,
          title: '',
          description: '',
          enabled: true,
        }
        return;
      }

      this.loading = true;

      api.getReleaseById(payload,
        (res) => {
          this.data = res;
              const { year, month, day } = res.releaseDate;
              const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              this.releaseDate = iso;

            this.releaseDateFormatted = this.formatISOStringDateToLocalDate(this.releaseDate);
          this.loading = false;
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.data.releaseDate = this.releaseDate
        ? this.convertDateToSimpleDate(new Date(this.releaseDate))
        : null;

      this.loading = true;

      api.storeRelease(this.data,
        (res) => {
          this.loading = false;
          if (res) {
            this.$emit('refresh');
            this.showDialog = false;
          }
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    confirmDelete() {
        this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.deleteReleaseById(this.data.id,
        () => {
          this.loading = false;
          this.$emit("refresh");
          this.showDialog = false;
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    updateFormattedDate() {
      if (this.releaseDate) {
        this.releaseDateFormatted = this.formatISOStringDateToLocalDate(this.releaseDate);
      }
      if (this.toDate) {
        this.toDateFormatted = this.formatISOStringDateToLocalDate(this.toDate);
      }
    }
  },
  watch: {
    releaseDate() {
      this.updateFormattedDate();
    }
  }
}
</script>
